import { useLoadMails } from 'modules/Mails/hooks/use-load-mails';
import posthog from 'posthog-js';
import { useHistory } from 'react-router';
import { first, isDefined, last } from 'remeda';
import { captureEvent, type MailActionOptions } from 'shared/analytics/capture-event';

export const useMailDetailNavigation = (id: string) => {
  const history = useHistory();

  const { mails } = useLoadMails('inbox', {
    status: 'unprocessed',
    sortBy: 'date',
    sortOrder: 'desc',
    pageLimit: 100,
  });

  const unprocessedList = mails.threads;
  const unprocessedTotal = mails.pagination.totalObjects;
  const currentIndex = unprocessedList.findIndex(item => item.id === id);
  const isCurrentInQueue = currentIndex >= 0;

  const prevId = isCurrentInQueue
    ? unprocessedList[currentIndex - 1]?.id
    : last(unprocessedList)?.id;
  const nextId = isCurrentInQueue
    ? unprocessedList[currentIndex + 1]?.id
    : first(unprocessedList)?.id;

  const goToPrev = ({ trigger }: MailActionOptions) => {
    if (!prevId) return;
    captureEvent(posthog, 'mail:thread_navigated', { direction: 'prev', trigger });
    history.replace(`/mails/${prevId}`);
  };

  const goToNext = ({ trigger }: MailActionOptions) => {
    if (!nextId) return;
    captureEvent(posthog, 'mail:thread_navigated', { direction: 'next', trigger });
    history.replace(`/mails/${nextId}`);
  };

  if (unprocessedTotal === 0)
    return { unprocessedNavigation: null, prevId, nextId, goToPrev, goToNext };

  return {
    unprocessedNavigation: {
      total: unprocessedTotal,
      currentPosition: isCurrentInQueue ? currentIndex + 1 : null,
      hasPrev: isDefined(prevId),
      hasNext: isDefined(nextId),
      onPrev: goToPrev,
      onNext: goToNext,
    },
    prevId,
    nextId,
    goToPrev,
    goToNext,
  };
};
